import { Link } from "@tanstack/react-router";
import { Check, AlertTriangle, ShieldCheck, Clock, Award, Wrench } from "lucide-react";
import type { Service } from "@/lib/services";
import { SERVICES } from "@/lib/services";
import { CITIES, CITY_BY_SLUG, CITY_SERVICE_BY_SLUG } from "@/lib/cities";
import { INDEXED_COMBOS, COMBO_SHORT_NAME } from "@/lib/indexed-combos";
import { isKeptCityService } from "@/lib/kept-city-services";
import { BUSINESS } from "@/lib/business";
import { QuoteForm } from "./QuoteForm";
import { TrustBar } from "./TrustBar";
import { Testimonials } from "./Testimonials";
import { FAQ } from "./FAQ";
import { CTABand } from "./CTABand";
import { RelatedReading } from "./RelatedReading";
import { PhoneButton } from "./PhoneButton";
import { SERVICE_BODY_HTML, SERVICE_EXTRA_HTML } from "@/lib/service-body";
import techWorking from "@/assets/tech-working.webp";
import emergencyResponse from "@/assets/emergency-response.webp";
import waterHeaterWork from "@/assets/water-heater-work.webp";
import plumberInspection from "@/assets/plumber-inspection.webp";
import drainCleaning from "@/assets/drain-cleaning.webp";
import faucetInstall from "@/assets/faucet-install.webp";
import pipeRepair from "@/assets/pipe-repair.webp";
import serviceVan from "@/assets/service-van.webp";
import homeFleet from "@/assets/home-fleet.webp";
import homeHero from "@/assets/home-hero.webp";

function pickHeroImage(slug: string) {
  if (slug.includes("emergency")) return emergencyResponse;
  if (slug.includes("water-heater")) return waterHeaterWork;
  if (slug.includes("drain")) return drainCleaning;
  if (slug.includes("sewer") || slug.includes("pipe-repair")) return pipeRepair;
  if (slug.includes("commercial")) return serviceVan;
  if (slug.includes("new-construction")) return homeFleet;
  if (slug.includes("faucet") || slug.includes("toilet") || slug.includes("kitchen-and-bathroom")) return faucetInstall;
  if (slug.includes("leak") || slug.includes("inspection")) return plumberInspection;
  if (slug.includes("residential")) return homeHero;
  return techWorking;
}

function pickServiceImage(slug: string) {
  if (slug.includes("drain")) return drainCleaning;
  if (slug.includes("sewer")) return pipeRepair;
  if (slug.includes("pipe-repair") || slug.includes("repipe")) return pipeRepair;
  if (slug.includes("faucet") || slug.includes("fixture") || slug.includes("toilet")) return faucetInstall;
  if (slug.includes("water-heater")) return waterHeaterWork;
  if (slug.includes("inspection")) return plumberInspection;
  if (slug.includes("leak")) return plumberInspection;
  if (slug.includes("emergency")) return emergencyResponse;
  if (slug.includes("gas")) return techWorking;
  if (slug.includes("commercial")) return serviceVan;
  if (slug.includes("new-construction")) return homeFleet;
  if (slug.includes("kitchen-and-bathroom") || slug.includes("bathroom-remodel") || slug.includes("kitchen-remodel")) return faucetInstall;
  if (slug.includes("filtration") || slug.includes("softener")) return serviceVan;
  if (slug.includes("backflow")) return techWorking;
  if (slug.includes("garbage-disposal")) return faucetInstall;
  if (slug.includes("residential")) return homeHero;
  return plumberInspection;
}

export function ServicePageTemplate({ service, city }: { service: Service; city?: string }) {
  const cityLabel = city ?? "Phoenix";
  const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);
  const heroImg = pickHeroImage(service.slug);
  const comboLinks = Object.keys(INDEXED_COMBOS).filter(
    (k) => isKeptCityService(k.split("/")[0], k.split("/")[1]) && CITY_SERVICE_BY_SLUG[k.split("/")[1]]?.parentService === service.slug,
  );
  const bodyHtml = SERVICE_BODY_HTML[service.slug];
  const extraHtml = SERVICE_EXTRA_HTML[service.slug];
  // Same-day language is only honest on real repair/swap calls — not on scheduled
  // remodels, new construction, filtration, backflow tests, or inspections.
  const sameDayUnrealistic = [
    "water-filtration-and-softener-systems-phoenix-az",
    "bathroom-remodeling-plumbing-phoenix-az",
    "kitchen-remodeling-plumbing-phoenix-az",
    "new-construction-plumbing-phoenix-az",
    "backflow-prevention-and-testing-phoenix-az",
    "plumbing-inspections-and-maintenance",
  ].includes(service.slug);
  const heroLede = sameDayUnrealistic
    ? `Scheduled ${service.name.toLowerCase()} from a family-owned ${cityLabel} shop`
    : `Same-day ${service.name.toLowerCase()} from a family-owned ${cityLabel} shop`;
  return (
    <>
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="border-b border-border bg-muted/30">
        <ol className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-2 text-xs text-muted-foreground">
          <li><Link to="/" className="hover:text-primary">Home</Link></li>
          <li aria-hidden>›</li>
          <li aria-current="page" className="text-secondary font-semibold">{service.name}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-secondary to-secondary/90 text-secondary-foreground overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImg})` }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/80 to-secondary/45" />
        <div className="relative mx-auto max-w-7xl px-4 py-10 md:py-16 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <p className="text-accent font-bold text-sm uppercase tracking-widest">{cityLabel}, AZ</p>
            <h1 className="mt-2 text-3xl md:text-5xl font-extrabold tracking-tight">
              {service.h1 ?? `${service.name} in ${cityLabel}, AZ — Licensed & Insured`}
            </h1>
            <p className="mt-5 text-lg text-secondary-foreground/90 leading-relaxed">
              {heroLede} — {BUSINESS.license}, serving the Valley since {BUSINESS.founded}. Upfront flat pricing, no overtime gouging, written workmanship
              warranty on every job. Call{" "}
              <a href={BUSINESS.phoneHref} className="font-extrabold text-accent hover:underline">{BUSINESS.phone}</a>{" "}
              or request a free quote in 60 seconds.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <PhoneButton size="lg" />
              <a href="#quote" className="inline-flex items-center justify-center rounded-md border-2 border-accent px-6 py-4 text-base font-extrabold text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                Get Free Quote
              </a>
            </div>
            <div className="mt-6"><TrustBar dark /></div>
          </div>
          <div id="quote" className="lg:mt-2">
            <QuoteForm defaultService={service.name} />
          </div>
        </div>
      </section>

      {/* Intro with image */}
      <section className="mx-auto max-w-7xl px-4 py-12 grid gap-10 lg:grid-cols-2 items-center">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
            About Our {service.name} Service in {cityLabel}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-foreground">{service.intro}</p>
          {service.intro2 && (
            <p className="mt-4 text-foreground leading-relaxed">{service.intro2}</p>
          )}
        </div>
        <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border">
          <img
            decoding="async"
            src={pickServiceImage(service.slug)}
            alt={`${service.name} service in ${cityLabel}`}
            width={1264}
            height={848}
            className="w-full h-auto block aspect-[1264/848] object-cover"
            loading="lazy"
          />
        </div>
      </section>

      {/* Long-form keyword-rich body content, per-page */}
      {bodyHtml && (
        <section className="bg-muted/30 border-y border-border">
          <article
            className="mx-auto max-w-4xl px-4 py-12 blog-content"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        </section>
      )}

      {/* What we fix */}
      <section className="mx-auto max-w-7xl px-4 pb-12">
        <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">What's Included with {service.name}</h2>
        <p className="mt-2 text-muted-foreground max-w-3xl">
          The most common {service.name.toLowerCase()} jobs we handle for {cityLabel} homeowners. Don't see your
          issue? Call us — if it's plumbing, we fix it.
        </p>
        <ul className="mt-6 grid gap-3 md:grid-cols-2">
          {service.whatWeFix.map((w) => (
            <li key={w} className="flex gap-3 rounded-lg border border-border bg-card p-4">
              <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
              <span className="font-semibold text-secondary">{w}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Warning signs — only when service-specific signs are provided */}
      {service.warningSigns && service.warningSigns.length > 0 && (
        <section className="bg-secondary/[0.03] border-y border-border">
          <div className="mx-auto max-w-7xl px-4 py-12">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-7 w-7 text-accent" />
              <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
                Signs You Need {service.name} in {cityLabel}
              </h2>
            </div>
            <p className="mt-3 text-foreground max-w-3xl">
              These are the symptoms we see most often on {service.name.toLowerCase()} calls in {cityLabel}. If
              any of them sound familiar, get it looked at before the damage outruns the repair.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {service.warningSigns.map((s) => (
                <div key={s.t} className="rounded-xl border border-border bg-card p-5">
                  <div className="font-extrabold text-secondary">{s.t}</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Step-specific keyword section */}
      {extraHtml && (
        <section className="border-y border-border">
          <article
            className="mx-auto max-w-4xl px-4 py-12 blog-content"
            dangerouslySetInnerHTML={{ __html: extraHtml }}
          />
        </section>
      )}

      {/* Pricing */}
      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 py-12 grid gap-8 md:grid-cols-2 items-center">
          <div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">Honest, Upfront Pricing</h2>
            <p className="mt-3 text-foreground">
              {service.pricingNote ??
                `Every ${service.name.toLowerCase()} quote is flat-rate and written down before any work begins — no hourly surprises, no after-the-fact add-ons.`}
            </p>
            {service.pricingBullets && service.pricingBullets.length > 0 && (
              <ul className="mt-5 space-y-2 text-sm text-foreground">
                {service.pricingBullets.map((b) => (
                  <li key={b} className="flex gap-2">
                    <Check className="h-4 w-4 text-success mt-1 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl border-2 border-accent bg-card p-6 shadow-md">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Typical {cityLabel} pricing</div>
            <div className="mt-2 text-xl font-extrabold text-secondary">{service.priceRange}</div>
            <PhoneButton className="mt-5 w-full justify-center" />
          </div>
        </div>
      </section>

      {/* Process — render only when steps provided */}
      {service.processSteps && service.processSteps.length > 0 && (
      <section className="mx-auto max-w-7xl px-4 py-12 grid gap-10 lg:grid-cols-[1.2fr_1fr] items-center">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">Our {service.name} Process</h2>
          <p className="mt-3 text-foreground max-w-2xl">
            {service.processNote ??
              `How a typical ${service.name.toLowerCase()} job runs in ${cityLabel} from the first call to the final walkthrough.`}
          </p>
          <ol className="mt-6 grid gap-5 md:grid-cols-2">
            {service.processSteps.map((s) => (
              <li key={s.n} className="rounded-xl bg-card border border-border p-5">
                <div className="text-3xl font-black text-primary">{s.n}</div>
                <div className="mt-1 font-bold text-secondary">{s.t}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border">
          <img
            decoding="async"
            src={pickServiceImage(service.slug)}
            alt={`Licensed plumber working in ${cityLabel} home`}
            width={1264}
            height={848}
            className="w-full h-auto block aspect-[1264/848] object-cover"
            loading="lazy"
          />
        </div>
      </section>
      )}

      {/* Why us */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-extrabold">
            Why {cityLabel} Chooses Phoenix Plumbing Co.
          </h2>
          <p className="mt-3 text-secondary-foreground/85 max-w-3xl">
            We're not a national chain or a private-equity-owned brand. We're a family shop, three generations
            deep in the Valley, and our reputation is the only thing keeping the trucks moving.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { Icon: ShieldCheck, t: "Licensed & Insured", d: `${BUSINESS.license} • $2M general liability • bonded for your protection.` },
              { Icon: Clock, t: "Local Dispatch", d: "Real local plumbers answer calls during business hours. Emergency plumbing calls accepted by phone after hours." },
              { Icon: Award, t: "Trusted Since 2012", d: "Trusted by thousands of Arizona homeowners since 2012 — our reputation keeps the trucks moving." },
              { Icon: Wrench, t: "Written Warranty", d: "Every repair and install backed by our workmanship guarantee — in writing, on every invoice." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="rounded-xl border border-secondary-foreground/10 bg-secondary-foreground/5 p-5">
                <Icon className="h-7 w-7 text-accent" />
                <div className="mt-3 font-extrabold text-accent">{t}</div>
                <p className="mt-2 text-sm text-secondary-foreground/85 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">What {cityLabel} Customers Say</h2>
          <div className="mt-6"><Testimonials count={3} city={city} /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-extrabold text-secondary mb-6">Frequently Asked Questions</h2>
        <FAQ items={service.faqs} />
      </section>

      {/* Related */}
      <section className="mx-auto max-w-7xl px-4 pb-12">
        <h2 className="text-2xl font-extrabold text-secondary mb-4">Related Services</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {related.map((s) => (
            <Link key={s.slug} to={`/${s.slug}` as any} className="rounded-lg border border-border bg-card p-4 hover:border-primary transition-colors">
              <div className="font-bold text-secondary">{s.name}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.short}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Service Areas — internal links to every city page */}
      <section className="mx-auto max-w-7xl px-4 pb-12">
        <h2 className="text-2xl font-extrabold text-secondary mb-2">
          {service.name} — Service Areas Across the Valley
        </h2>
        <p className="text-muted-foreground mb-4 max-w-3xl">
          We dispatch licensed plumbers for {service.name.toLowerCase()} across all of greater Phoenix. Pick your city for local response times and pricing.
        </p>
        <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-4">
          {CITIES.map((c) => (
            <Link
              key={c.slug}
              to="/areas-served/$city"
              params={{ city: c.slug }}
              className="rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold text-secondary hover:border-primary hover:text-primary transition-colors"
            >
              {service.name} in {c.name}
            </Link>
          ))}
          {comboLinks.map((k) => {
            const [cs, ss] = k.split("/");
            return (
              <Link
                key={k}
                to="/areas-served/$city/$service"
                params={{ city: cs, service: ss }}
                className="rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold text-secondary hover:border-primary hover:text-primary transition-colors"
              >
                {COMBO_SHORT_NAME[ss]} in {CITY_BY_SLUG[cs]?.name}, AZ
              </Link>
            );
          })}
        </div>
      </section>

      <RelatedReading slug={service.slug} />

      <CTABand />
    </>
  );
}